/**
 * Device fingerprinting utilities for risk assessment
 * Uses browser APIs to create a semi-unique device identifier
 */

export interface DeviceInfo {
  fingerprint: string
  browser: string
  os: string
  screenResolution: string
  timezone: string
  language: string
  platform: string
  colorDepth: number
  touchSupport: boolean
}

/**
 * Generate a simple hash from a string
 */
function simpleHash(str: string): string {
  let hash = 0
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i)
    hash = ((hash << 5) - hash) + char
    hash = hash & hash // Convert to 32bit integer
  }
  return Math.abs(hash).toString(36)
}

/**
 * Detect browser from user agent
 */
function detectBrowser(): string {
  const ua = navigator.userAgent
  if (ua.includes('Firefox')) return 'Firefox'
  if (ua.includes('Edg')) return 'Edge'
  if (ua.includes('Chrome')) return 'Chrome'
  if (ua.includes('Safari')) return 'Safari'
  if (ua.includes('Opera') || ua.includes('OPR')) return 'Opera'
  return 'Unknown'
}

/**
 * Detect OS from user agent
 */
function detectOS(): string {
  const ua = navigator.userAgent
  if (ua.includes('Windows')) return 'Windows'
  if (ua.includes('Mac OS')) return 'macOS'
  if (ua.includes('Linux')) return 'Linux'
  if (ua.includes('Android')) return 'Android'
  if (ua.includes('iOS') || ua.includes('iPhone') || ua.includes('iPad')) return 'iOS'
  return 'Unknown'
}

/**
 * Collect device information and generate fingerprint
 */
export function getDeviceInfo(): DeviceInfo {
  const browser = detectBrowser()
  const os = detectOS()
  const screenResolution = `${screen.width}x${screen.height}`
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone
  const language = navigator.language
  const platform = navigator.platform
  const colorDepth = screen.colorDepth
  const touchSupport = 'ontouchstart' in window || navigator.maxTouchPoints > 0

  // Create fingerprint from device characteristics
  const fingerprintData = [
    browser,
    os,
    screenResolution,
    timezone,
    language,
    platform,
    colorDepth.toString(),
    touchSupport.toString(),
    navigator.hardwareConcurrency?.toString() || '0',
    (navigator as any).deviceMemory?.toString() || '0'
  ].join('|')

  const fingerprint = simpleHash(fingerprintData)

  return {
    fingerprint,
    browser,
    os,
    screenResolution,
    timezone,
    language,
    platform,
    colorDepth,
    touchSupport
  }
}

/**
 * Get a short device description for display
 */
export function getDeviceDescription(): string {
  const info = getDeviceInfo()
  return `${info.browser} on ${info.os}`
}
